/**
 * WISE Web Service - Product 95: COMMERCIO_PERSONA_FISICA
 * Validation Helpers and Utilities
 *
 * Common helper functions for validation scenarios
 */

import {
  GUARANTEE_CATEGORIES,
  CAPITAL_LIMITS,
  INCOMPATIBLE_GUARANTEES,
  REQUIRED_GUARANTEE_COMBINATIONS,
} from './validationRulesProduct95';

// ============================================================================
// GUARANTEE HELPERS
// ============================================================================

/**
 * Get guarantee category for a given guarantee code
 * @param {string} guaranteeCode - Guarantee code
 * @returns {Object|null} Category object or null
 */
export function getGuaranteeCategory(guaranteeCode) {
  const entry = Object.entries(GUARANTEE_CATEGORIES).find(([, category]) => category.guarantees.includes(guaranteeCode));
  if (entry) {
    const [key, category] = entry;
    return { ...category, key };
  }
  return null;
}

/**
 * Check if two guarantees are incompatible
 * @param {string} guarantee1 - First guarantee code
 * @param {string} guarantee2 - Second guarantee code
 * @returns {boolean}
 */
export function areGuaranteesIncompatible(guarantee1, guarantee2) {
  const incompatible = INCOMPATIBLE_GUARANTEES[guarantee1] || [];
  return incompatible.includes(guarantee2);
}

/**
 * Get list of guarantees required by a guarantee
 * @param {string} guaranteeCode - Guarantee code
 * @returns {Array<string>}
 */
export function getRequiredGuarantees(guaranteeCode) {
  return REQUIRED_GUARANTEE_COMBINATIONS[guaranteeCode] || [];
}

/**
 * Check if a guarantee requires RCT
 * @param {string} guaranteeCode - Guarantee code
 * @returns {boolean}
 */
export function requiresRCT(guaranteeCode) {
  const required = REQUIRED_GUARANTEE_COMBINATIONS[guaranteeCode] || [];
  return required.includes('RCT');
}

/**
 * Get the currently-selected guarantees that require the given guarantee
 * (i.e. guarantees that cannot be removed while these are still selected)
 * @param {string} coverageCode - Guarantee code being checked
 * @param {Array<string>} selectedCodes - Currently selected guarantee codes
 * @returns {Array<string>}
 */
export function getGuaranteesThatRequire(coverageCode, selectedCodes) {
  return selectedCodes.filter((code) => {
    if (code === coverageCode) return false;
    const required = REQUIRED_GUARANTEE_COMBINATIONS[code] || [];
    return required.some((req) => {
      if (Array.isArray(req)) {
        if (!req.includes(coverageCode)) return false;
        const otherMemberPresent = req.some(
          (c) => c !== coverageCode && selectedCodes.includes(c),
        );
        return !otherMemberPresent;
      }
      return req === coverageCode;
    });
  });
}

/**
 * Get incompatible guarantees for a given guarantee
 * @param {string} guaranteeCode - Guarantee code
 * @returns {Array<string>}
 */

/**
 * Get all guarantees that are compatible with the given guarantee
 * @param {string} guaranteeCode - Guarantee code
 * @param {Array<string>} allGuarantees - Array of all available guarantee codes
 * @returns {Array<string>}
 */
export function getCompatibleGuarantees(guaranteeCode, allGuarantees) {
  const incompatible = INCOMPATIBLE_GUARANTEES[guaranteeCode] || [];
  return allGuarantees.filter((g) => !incompatible.includes(g) && g !== guaranteeCode);
}

// ============================================================================
// CAPITAL HELPERS
// ============================================================================

/**
 * Check if capital amount is within valid range
 * @param {string} guaranteeCode - Guarantee code
 * @param {number} capital - Capital amount
 * @returns {Object} { isValid, isRecommended, message }
 */
export function validateCapitalRange(guaranteeCode, capital) {
  const limits = CAPITAL_LIMITS[guaranteeCode];

  if (!limits) {
    return { isValid: true, isRecommended: true, message: '' };
  }

  if (capital < limits.min) {
    return {
      isValid: false,
      isRecommended: false,
      message: `Capitale inferiore al minimo (€${limits.min.toLocaleString()})`,
    };
  }

  if (capital > limits.max && limits.max > 0) {
    return {
      isValid: false,
      isRecommended: false,
      message: `Capitale superiore al massimo (€${limits.max.toLocaleString()})`,
    };
  }

  if (capital < limits.recommended_min) {
    return {
      isValid: true,
      isRecommended: false,
      message: `Capitale inferiore al raccomandato (€${limits.recommended_min.toLocaleString()})`,
    };
  }

  if (capital > limits.recommended_max && limits.recommended_max > 0) {
    return {
      isValid: true,
      isRecommended: false,
      message: `Capitale superiore al raccomandato (€${limits.recommended_max.toLocaleString()})`,
    };
  }

  return { isValid: true, isRecommended: true, message: 'OK' };
}

/**
 * Get suggested capital based on property value
 * @param {string} guaranteeCode - Guarantee code
 * @param {number} propertyValue - Property value
 * @returns {number}
 */
export function getSuggestedCapital(guaranteeCode, propertyValue) {
  const limits = CAPITAL_LIMITS[guaranteeCode];

  if (!limits) {
    return 0;
  }

  // Default suggestions based on guarantee type
  const suggestions = {
    'CONT.F': propertyValue, // 100% of property value
    'CONT-8': propertyValue, // 100% of property value
    'FABB-9': propertyValue, // 100% of property value
    'FABB-8': propertyValue, // 100% of property value
    RILO: propertyValue * 0.25, // 25% of property value
    'RTER-8': propertyValue * 0.25, // 25% of property value
    'RTER-9': propertyValue * 0.25, // 25% of property value
    'REFR-8': 5000, // Fixed suggestion
    'REFR-9': 5000, // Fixed suggestion
    CRIS: 5000, // Fixed suggestion
  };

  let suggested = suggestions[guaranteeCode] || limits.recommended_min;

  // Clamp to limits
  suggested = Math.max(limits.min, Math.min(limits.max || Infinity, suggested));

  return Math.round(suggested);
}

// ============================================================================
// COVERAGE HELPERS
// ============================================================================

/**
 * Check if RCT is included in coverages
 * @param {Array} coverages - Array of coverage objects
 * @returns {boolean}
 */
export function hasRCT(coverages) {
  return coverages.some((c) => c.coverage_code === 'RCT');
}

/**
 * Get missing required guarantees for current selection
 * @param {Array} coverages - Array of coverage objects
 * @returns {Array<string>}
 */
export function getMissingRequiredGuarantees(coverages) {
  const selectedCodes = coverages.map((c) => c.coverage_code);
  const missing = new Set();

  selectedCodes.forEach((code) => {
    const required = REQUIRED_GUARANTEE_COMBINATIONS[code] || [];
    required.forEach((req) => {
      if (Array.isArray(req)) {
        const satisfied = req.some((c) => selectedCodes.includes(c));
        if (!satisfied) {
          req.forEach((c) => missing.add(c));
        }
        return;
      }
      if (!selectedCodes.includes(req)) {
        missing.add(req);
      }
    });
  });

  // RCT is always required
  if (selectedCodes.length > 0 && !selectedCodes.includes('RCT')) {
    missing.add('RCT');
  }

  return Array.from(missing);
}

/**
 * Recursively resolve every guarantee that must be auto-selected alongside
 * `goodId` so its Vendibilità requirements are satisfied. For an
 * OR-group requirement (e.g. FABB or RILO, mutually exclusive), resolves
 * to RILO when the building is "in Locazione" (`buildingType === '08'`),
 * else FABB — the same rule already used by the Tipo Fabbricato sync
 * effect — unless one member of the group is already selected.
 * @param {string} goodId - Guarantee code being checked
 * @param {Array<string>} currentCodes - Currently selected guarantee codes
 * @param {string} [buildingType] - technical_location_data.tpab_value ('07'|'08')
 * @returns {Array<string>} Codes to add, in the order they should be appended
 */
export function getRequiredGuaranteesToAdd(goodId, currentCodes, buildingType) {
  const toAdd = [];
  const known = new Set(currentCodes);
  const visited = new Set();

  const visit = (code) => {
    if (visited.has(code)) return;
    visited.add(code);
    const required = REQUIRED_GUARANTEE_COMBINATIONS[code] || [];

    required.forEach((req) => {
      if (Array.isArray(req)) {
        const alreadySatisfied = req.some((c) => known.has(c));
        if (alreadySatisfied) return;
        const pick = buildingType === '08' ? 'RILO' : 'FABB';
        if (!known.has(pick)) {
          known.add(pick);
          toAdd.push(pick);
          visit(pick);
        }
        return;
      }
      if (!known.has(req)) {
        known.add(req);
        toAdd.push(req);
        visit(req);
      }
    });
  };

  visit(goodId);
  return toAdd;
}

/**
 * Transitive closure of `getGuaranteesThatRequire`: every currently-selected
 * guarantee that must also be removed if `goodId` is removed, because every
 * currently-selected way it has of satisfying its own requirement is itself
 * being removed.
 * @param {string} goodId - Guarantee code being unchecked
 * @param {Array<string>} currentCodes - Currently selected guarantee codes
 * @returns {Array<string>} Codes to remove alongside goodId (not including goodId itself)
 */
export function getDependentsToRemove(goodId, currentCodes) {
  const toRemove = new Set([goodId]);

  const dependsOnRemoved = (code) => {
    const required = REQUIRED_GUARANTEE_COMBINATIONS[code] || [];
    return required.some((req) => {
      if (Array.isArray(req)) {
        const selectedAlternatives = req.filter((c) => currentCodes.includes(c));
        return selectedAlternatives.length > 0
          && selectedAlternatives.every((c) => toRemove.has(c));
      }
      return toRemove.has(req);
    });
  };

  const findNewDependents = () => currentCodes.filter(
    (code) => !toRemove.has(code) && dependsOnRemoved(code),
  );

  let newlyFound = findNewDependents();
  while (newlyFound.length > 0) {
    newlyFound.forEach((code) => toRemove.add(code));
    newlyFound = findNewDependents();
  }

  toRemove.delete(goodId);
  return Array.from(toRemove);
}

/**
 * Get all incompatible guarantees in current selection
 * @param {Array} coverages - Array of coverage objects
 * @returns {Array<Object>} Array of { guarantee1, guarantee2 } pairs
 */
export function getIncompatiblePairs(coverages) {
  const selectedCodes = coverages.map((c) => c.coverage_code);
  const pairs = [];

  selectedCodes.forEach((code1, i) => {
    selectedCodes.slice(i + 1).forEach((code2) => {
      if (areGuaranteesIncompatible(code1, code2)) {
        pairs.push({ guarantee1: code1, guarantee2: code2 });
      }
    });
  });

  return pairs;
}

// ============================================================================
// FORMAT HELPERS
// ============================================================================

/**
 * Format capital amount for display
 * @param {number} amount - Amount to format
 * @returns {string}
 */
export function formatCapital(amount) {
  if (!amount) return '€0';
  return `€${amount.toLocaleString('it-IT')}`;
}

/**
 * Format date for display
 * @param {string|Date} date - Date to format
 * @returns {string}
 */
export function formatDate(date) {
  if (!date) return '';

  const d = new Date(date);
  return d.toLocaleDateString('it-IT', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Format validation error for user display
 * @param {Object} error - Validation error object
 * @returns {string}
 */
export function formatValidationError(error) {
  let message = error.message;

  if (error.details && Object.keys(error.details).length > 0) {
    const detailsStr = Object.entries(error.details)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
    message += ` (${detailsStr})`;
  }

  return message;
}

// ============================================================================
// VALIDATION SUMMARY HELPERS
// ============================================================================

/**
 * Get validation summary statistics
 * @param {Object} validationResult - Validation result object
 * @returns {Object}
 */
export function getValidationSummary(validationResult) {
  return {
    totalIssues: (validationResult.errors?.length || 0)
                 + (validationResult.warnings?.length || 0)
                 + (validationResult.suggestions?.length || 0),
    blockingErrors: validationResult.errors?.length || 0,
    warnings: validationResult.warnings?.length || 0,
    suggestions: validationResult.suggestions?.length || 0,
    isValid: validationResult.isValid !== false && (validationResult.errors?.length || 0) === 0,
    hasWarnings: (validationResult.warnings?.length || 0) > 0,
    hasSuggestions: (validationResult.suggestions?.length || 0) > 0,
  };
}

/**
 * Group validation errors by category
 * @param {Array} errors - Array of error objects
 * @returns {Object}
 */
export function groupErrorsByCategory(errors) {
  const grouped = {
    guarantees: [],
    parameters: [],
    capitals: [],
    dates: [],
    anagraphic: [],
    technical: [],
    other: [],
  };

  errors.forEach((error) => {
    if (error.code.includes('GUARANTEE') || error.code.includes('COMBINATION')) {
      grouped.guarantees.push(error);
    } else if (error.code.includes('PARAMETER')) {
      grouped.parameters.push(error);
    } else if (error.code.includes('CAPITAL')) {
      grouped.capitals.push(error);
    } else if (error.code.includes('DATE') || error.code.includes('TEMPORAL')) {
      grouped.dates.push(error);
    } else if (error.code.includes('FISCAL') || error.code.includes('AGE') || error.code.includes('GENDER')) {
      grouped.anagraphic.push(error);
    } else if (error.code.includes('TECHNICAL')) {
      grouped.technical.push(error);
    } else {
      grouped.other.push(error);
    }
  });

  return grouped;
}

// ============================================================================
// EXPORT ALL HELPERS
// ============================================================================

export default {
  // Guarantee helpers
  getGuaranteeCategory,
  areGuaranteesIncompatible,
  getRequiredGuarantees,
  requiresRCT,
  getGuaranteesThatRequire,
  getRequiredGuaranteesToAdd,
  getDependentsToRemove,

  getCompatibleGuarantees,

  // Capital helpers
  validateCapitalRange,
  getSuggestedCapital,

  // Coverage helpers
  hasRCT,
  getMissingRequiredGuarantees,
  getIncompatiblePairs,

  // Format helpers
  formatCapital,
  formatDate,
  formatValidationError,

  // Summary helpers
  getValidationSummary,
  groupErrorsByCategory,
};
